import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';
import InfoIcon from '@mui/icons-material/Info';
import { Button, Card, Chip, TextField, Typography } from '@mui/material';
import { OneMedia, useAdvice } from '@prismamedia/one-components';
import { EditorState, RawDraftContentState } from 'draft-js';
import { getSelectionEntity } from 'draftjs-utils';
import { findKey, isUndefined } from 'lodash';
import React, {
  ChangeEvent,
  Dispatch,
  FC,
  MutableRefObject,
  SetStateAction,
  memo,
  useCallback,
  useRef,
  useState,
} from 'react';
import { ArticleFormat } from '../../../__generated__/queries-web';
import { DraftWithBlockCounter } from '../../../components/Draft/DraftWithBlockCounter';
import { CharCounter } from '../../../components/Draft/CharCounter';
import {
  PossibleActions,
  actions,
  actionsCustom,
} from '../../../components/Draft/DraftActions';
import { MediaInput } from '../../../components/MediaInput';
import { TextSuggestion } from '../../../components/TextSuggestion';
import { getBrandOptions } from '../../../utils/brands';
import {
  ArticleEditAction,
  ArticleState,
  actions as reducerActions,
} from '../../ArticleEdit/reducer';
import {
  renderMediaFromTemplate,
  renderPluginsFromTemplate,
} from '../../ArticleEdit/utils/renderFromTemplate';
import { useGetHandlePastedFn } from './handlePaste';
import {
  ArticleEditorContentPopper,
  ArticleEditorDiscoverTitlePopper,
  ArticleEditorLeadPopper,
  ArticleEditorTitlePopper,
} from './poppers';

import { ExpandLess, ExpandMore } from '@mui/icons-material';
import ReactGA from 'react-ga4';
import { AffilizzMediaPlugin } from '../plugins/affilizz';
import { AuthorsInput } from './AuthorsInput';
import { SlugInput } from './SlugInput';
import { ExternalSourcesInput } from './ExternalSourcesInput';
import { LINK_TITLE_IA_SUGGESTIONS } from './constants';
import { useStyles } from './styles';

type ArticleEditorProps = {
  articleEditWrapperRef: MutableRefObject<HTMLElement | null>;
  renderInput: (inputName: string) => boolean;
  dispatch: Dispatch<ArticleEditAction>;
  subjectId?: string;
  publicUrl: string | null;
  SourceComponent: React.ReactNode | null;
  CategoriesComponent: React.ReactNode | null;
  showBlockCounters?: boolean;
} & Pick<
  ArticleState,
  | 'id'
  | 'title'
  | 'secondaryTitle'
  | 'partnerTitle'
  | 'discoverTitle'
  | 'medias'
  | 'format'
  | 'brandKey'
  | 'lead'
  | 'body'
  | 'platformId'
  | 'status'
  | 'authors'
  | 'slug'
  | 'urls'
  | 'publishedAt'
  | 'bibliographies'
>;

const getIsEntityMapHasPersonLink = (entityMap: any) =>
  !isUndefined(findKey(entityMap, ['type', PossibleActions.PERSON_LINK]));

const getLinkTitleIa = (body: RawDraftContentState | null) => {
  const contentArticle = body?.blocks?.reduce((accum, line) => {
    if (line.text.length) {
      return `${accum} ${line.text
        .replace(/[’]/g, "'")
        .replace(/[^\wéèêëîïôöùûüàáâç,.?!;:'\n\s]|_/g, '')} `;
    }
    return accum;
  }, '');
  const base64data = new Buffer(contentArticle ?? '').toString('base64');
  return `${LINK_TITLE_IA_SUGGESTIONS}&text=${base64data}`;
};

export const ArticleEditor: FC<ArticleEditorProps> = memo(
  ({
    id,
    articleEditWrapperRef,
    title,
    secondaryTitle,
    partnerTitle,
    discoverTitle,
    medias,
    lead,
    body,
    renderInput,
    format,
    brandKey,
    dispatch,
    subjectId,
    platformId,
    publicUrl,
    authors,
    slug,
    urls,
    publishedAt,
    bibliographies,
    CategoriesComponent = null,
    SourceComponent = null,
    showBlockCounters = true,
  }) => {
    const classes = useStyles();
    const { openAdvice } = useAdvice();
    const contentFieldRef = useRef(null);
    const leadFieldRef = useRef(null);
    const titleFieldRef = useRef(null);
    const discoverTitleFieldRef = useRef(null);
    const partnerTitleFieldRef = useRef<HTMLDivElement | null>(null);
    const [showPersonLinkAction, setShowPersonLinkAction] = useState<boolean>(
      getIsEntityMapHasPersonLink(body?.entityMap),
    );

    const [displayTitles, setDisplayTitles] = useState<boolean>(() => {
      // Load preference from localStorage, default to true if not set
      const saved = localStorage.getItem('article-editor-display-titles');
      return saved !== null ? JSON.parse(saved) : true;
    });

    // Save preference whenever it changes
    const toggleDisplayTitles = useCallback(() => {
      setDisplayTitles((prev) => {
        const newValue = !prev;
        localStorage.setItem(
          'article-editor-display-titles',
          JSON.stringify(newValue),
        );
        return newValue;
      });
    }, []);

    const [
      suggestMenuAnchor,
      setSuggestMenuAnchor,
    ] = useState<HTMLDivElement | null>(null);
    const [
      discoverTitleSuggestMenuAnchor,
      setDiscoverTitleSuggestMenuAnchor,
    ] = useState<HTMLDivElement | null>(null);
    const { isCerise } = getBrandOptions(brandKey);

    const setMedias = (updater: SetStateAction<OneMedia[]>) => {
      const newMedias =
        typeof updater === 'function' ? updater(medias || []) : updater;

      dispatch(
        reducerActions.updateField({ field: 'medias', value: newMedias }),
      );
    };

    const handleInfoIconClick = useCallback(
      (anchorRef, content) => () => {
        openAdvice({
          anchorRef,
          content,
          PopperProps: {
            container: articleEditWrapperRef?.current,
            keepMounted: false,
            modifiers: [
              {
                name: 'preventOverflow',
                options: {
                  boundariesElement: 'window',
                  enabled: false,
                  escapeWithReference: true,
                },
              },
            ],
            style: { width: '310px' },
          },
          zIndex: 1,
        });
      },
      [articleEditWrapperRef, openAdvice],
    );

    const handlePastedBody = useGetHandlePastedFn({
      dispatch,
      field: 'body',
    });

    const handleDraftSelection = useCallback((editorState: EditorState) => {
      let entity;

      const currentContent = editorState.getCurrentContent();
      const entityKey = getSelectionEntity(editorState);

      if (entityKey) {
        entity = currentContent.getEntity(entityKey);
      }

      entity?.getType() &&
        setShowPersonLinkAction(
          entity.getType() === PossibleActions.PERSON_LINK,
        );
    }, []);

    if (!brandKey) {
      return <Typography>Erreur, il n'y a pas de BrandKey</Typography>;
    }

    const { enableAffilizPlugin } = getBrandOptions(brandKey);
    const defaultPlugins = renderPluginsFromTemplate(brandKey);
    const affilizPlugin = AffilizzMediaPlugin(brandKey, id, format, dispatch);

    const plugins =
      enableAffilizPlugin && affilizPlugin
        ? [...defaultPlugins, affilizPlugin]
        : defaultPlugins;

    const allowedMedias = renderMediaFromTemplate(format, brandKey);
    const isVideo = format === ArticleFormat.Video;

    const linkTitleIa = getLinkTitleIa(body);

    const displayButtonTitleIa =
      !isVideo && getBrandOptions(brandKey).displayIATitle;

    const renderTitle = () => {
      if (!renderInput('title')) return null;

      return (
        <div className={classes.formGroup}>
          <TextField
            data-testid="title"
            fullWidth
            InputLabelProps={{
              shrink: true,
            }}
            InputProps={{
              startAdornment: displayButtonTitleIa && (
                <Chip
                  label="IA"
                  target="_blank"
                  component="a"
                  href={linkTitleIa}
                  variant="outlined"
                  color="default"
                  clickable
                  className={classes.textFieldChip}
                  onClick={() => {
                    ReactGA.event({
                      category: 'Article IA Titre principal',
                      action: 'Article IA Generation',
                      label: window.location.href,
                    });
                  }}
                  avatar={<AutoFixHighIcon />}
                />
              ),
            }}
            label={
              <div className={classes.textFieldLabelIcon}>
                <div>Titre</div>
                <InfoIcon
                  onClick={handleInfoIconClick(
                    titleFieldRef,
                    ArticleEditorTitlePopper,
                  )}
                  ref={titleFieldRef}
                />
              </div>
            }
            multiline
            onChange={(e: ChangeEvent<HTMLInputElement>) => {
              dispatch(
                reducerActions.updateField({
                  field: 'title',
                  value: e.currentTarget.value,
                }),
              );
            }}
            value={title}
            variant="standard"
          />
          <CharCounter content={title} />
        </div>
      );
    };

    return (
      <>
        <div className={classes.root}>
          {CategoriesComponent}
          <Card sx={{ p: 2, display: 'flex', flexDirection: 'column' }}>
            {renderTitle()}
            {displayTitles && (
              <>
                {renderInput('discoverTitle') && (
                  <TextField
                    data-testid="discoverTitle"
                    fullWidth
                    InputLabelProps={{
                      shrink: true,
                    }}
                    label={
                      <div className={classes.textFieldLabelIcon}>
                        <div>Titre discover</div>
                        <InfoIcon
                          onClick={handleInfoIconClick(
                            discoverTitleFieldRef,
                            ArticleEditorDiscoverTitlePopper,
                          )}
                          ref={discoverTitleFieldRef}
                        />
                      </div>
                    }
                    multiline
                    onChange={(e: ChangeEvent<HTMLInputElement>) => {
                      dispatch(
                        reducerActions.updateField({
                          field: 'discoverTitle',
                          value: e.currentTarget.value,
                        }),
                      );
                    }}
                    onClick={() => {
                      if (title && !discoverTitleSuggestMenuAnchor) {
                        setDiscoverTitleSuggestMenuAnchor(
                          discoverTitleFieldRef.current,
                        );
                      }
                    }}
                    value={discoverTitle || ''}
                    variant="standard"
                    ref={discoverTitleFieldRef}
                    sx={{ mt: 2 }}
                  />
                )}
                {renderInput('partnerTitle') && (
                  <TextField
                    data-testid="partnerTitle"
                    fullWidth
                    label={"Titre d'accroche"}
                    multiline
                    onClick={() => {
                      if (title && !suggestMenuAnchor) {
                        setSuggestMenuAnchor(partnerTitleFieldRef.current);
                      }
                    }}
                    onChange={(e: ChangeEvent<HTMLInputElement>) => {
                      dispatch(
                        reducerActions.updateField({
                          field: 'partnerTitle',
                          value: e.currentTarget.value,
                        }),
                      );
                    }}
                    value={partnerTitle || ''}
                    variant="standard"
                    ref={partnerTitleFieldRef}
                    sx={{ mt: 2 }}
                  />
                )}
                {renderInput('slug') && (
                  <>
                    <SlugInput
                      slug={slug}
                      title={title}
                      dispatch={dispatch}
                      sx={{ mt: 2 }}
                    />
                    {urls.map(({ path }, index) => (
                      <TextField
                        disabled
                        key={index}
                        data-testid="urls"
                        fullWidth
                        label="Urls"
                        multiline
                        value={path}
                        variant="standard"
                        sx={{ mt: 2 }}
                      />
                    ))}
                  </>
                )}
              </>
            )}
            <Button
              onClick={toggleDisplayTitles}
              sx={{ alignSelf: 'flex-end', mt: 2 }}
              startIcon={displayTitles ? <ExpandLess /> : <ExpandMore />}
            >
              {displayTitles ? 'Masquer' : 'Voir plus'}
            </Button>
          </Card>
          {title && suggestMenuAnchor && (
            <TextSuggestion
              anchorRef={suggestMenuAnchor}
              brandKey={brandKey}
              handleClose={() => setSuggestMenuAnchor(null)}
              fieldValue={partnerTitle || ''}
              field="partnerTitle"
              title={title}
              body={body?.blocks?.map((block) => block.text).join('\n') || ''}
              lead={lead?.blocks?.map((block) => block.text).join('\n') || ''}
              publishedAt={publishedAt || undefined}
              onConfirm={(suggestion: string) =>
                dispatch(
                  reducerActions.updateField({
                    field: 'partnerTitle',
                    value: suggestion,
                  }),
                )
              }
            />
          )}
          {title && discoverTitleSuggestMenuAnchor && (
            <TextSuggestion
              anchorRef={discoverTitleSuggestMenuAnchor}
              brandKey={brandKey}
              handleClose={() => setDiscoverTitleSuggestMenuAnchor(null)}
              fieldValue={discoverTitle || ''}
              field="discoverTitle"
              title={title}
              body={body?.blocks?.map((block) => block.text).join('\n') || ''}
              lead={lead?.blocks?.map((block) => block.text).join('\n') || ''}
              publishedAt={publishedAt || undefined}
              onConfirm={(suggestion: string) =>
                dispatch(
                  reducerActions.updateField({
                    field: 'discoverTitle',
                    value: suggestion,
                  }),
                )
              }
            />
          )}
          {renderInput('medias') && (
            <MediaInput
              medias={medias || []}
              title={isVideo ? 'Ajouter une vidéo' : undefined}
              disableFields={isVideo}
              allowedMedias={allowedMedias}
              brandKey={brandKey}
              setMedias={setMedias}
              subjectId={subjectId}
              videoId={platformId || undefined}
            />
          )}
          {renderInput('lightDraft') && (
            <div className={classes.formGroup}>
              <DraftWithBlockCounter
                placeholder={
                  <div className={classes.textFieldLabelIcon}>
                    <div>Chapô de l'article</div>
                    <InfoIcon
                      onClick={handleInfoIconClick(
                        leadFieldRef,
                        ArticleEditorLeadPopper,
                      )}
                      ref={leadFieldRef}
                    />
                  </div>
                }
                rawDraft={lead}
                onChange={(value) => {
                  dispatch(
                    reducerActions.updateField({
                      field: 'lead',
                      value,
                    }),
                  );
                }}
                actions={[actions.BOLD, actions.ITALIC]}
                showBlockCounters={showBlockCounters}
              />
            </div>
          )}
          {renderInput('richDraft') && (
            <div className={classes.formGroup}>
              <DraftWithBlockCounter
                placeholder={
                  <div className={classes.textFieldLabelIcon}>
                    <div>Contenu de l'article</div>
                    <InfoIcon
                      onClick={handleInfoIconClick(
                        contentFieldRef,
                        ArticleEditorContentPopper,
                      )}
                      ref={contentFieldRef}
                    />
                  </div>
                }
                rawDraft={body}
                onChange={(value) => {
                  dispatch(
                    reducerActions.updateField({
                      field: 'body',
                      value,
                    }),
                  );
                }}
                onSelection={handleDraftSelection}
                actions={[
                  actions.BOLD,
                  actions.ITALIC,
                  actions.STRIKETHROUGH,
                  actions.LINK,
                  actions.ANCHOR_LINK,
                  actions.INTERNAL_LINK,
                  actions.TITLE,
                  actions.HEADLINE,
                  actions.LIST,
                  actions.ORDERED_LIST,
                  actions.BLOCKQUOTE,
                  actionsCustom.COPY_ANCHORS(publicUrl ?? ''),
                  ...(showPersonLinkAction
                    ? [actions.SEPARATOR, actions.PERSON_LINK]
                    : []),
                ]}
                plugins={plugins}
                handlePastedText={handlePastedBody}
                showBlockCounters={showBlockCounters}
              />
            </div>
          )}
          <div className={classes.formGroup}>
            <AuthorsInput
              authors={authors}
              brandKey={brandKey}
              dispatch={dispatch}
              requiredInput={isCerise}
            />
            <TextField
              fullWidth
              label="Complément de signature"
              multiline
              onChange={(e: ChangeEvent<HTMLInputElement>) => {
                dispatch(
                  reducerActions.updateField({
                    field: 'secondaryTitle',
                    value: e.currentTarget.value,
                  }),
                );
              }}
              value={secondaryTitle || ''}
              variant="standard"
              sx={{ mt: 2 }}
            />
          </div>
          {SourceComponent}
          <ExternalSourcesInput
            bibliographies={bibliographies || []}
            dispatch={dispatch}
          />
        </div>
      </>
    );
  },
);
